/* eslint-disable @typescript-eslint/ban-ts-comment,@typescript-eslint/no-explicit-any */
import {
  filter,
  findIndex,
  get,
  isEmpty,
  isNil,
  isObject,
  isString,
  isUndefined,
  kebabCase,
  map,
  max,
  min,
  omitBy,
} from 'lodash';
import {
  adSectByPageType,
  adSubsectByPostCategory,
  adSubsectByPropertyType,
  AdUnits,
  DFP_PRICE_RANGE,
} from '../../helpers/ads/dfp';
import { DFPManager } from 'react-dfp';
import logger from './../logger/logger';
import { SCREEN_TYPE } from '../constants';
import { ScreenType } from '../utility/screenHelper';
import { Parent, RoomType } from '../../../queries/fragments/project';
import { LandingPageTypes } from '../domain/landingPages';
import { PropertyType } from '../domain/propertyTypes';
import { PostSubcategory } from '../domain/postCategories';

const SHOW_MERC_INTERVAL_NUMBER = 4;
const MERC_UNIT_NUMBER = 5;
const MILLION = 1000000;
const DEFAULT_SECT = '';
const DEFAULT_SUBSECT = '';

const strengthenKebabCase = (str: string) =>
  str ? kebabCase(str.replace(/[。、．，＇＂：；？|【】（）]/g, '-')) : '';

const convertDFPPrice = (priceTypes = {} as any): any => {
  let priceValue = get(priceTypes, 'starting_price');
  priceValue = isString(priceValue) ? priceValue.replace(/,/g, '') : priceValue;
  priceValue = priceValue < 10000 ? priceValue * MILLION : priceValue;
  if (priceValue) {
    const { convertedPriceRangeDFP, maxPriceForEachRange } = DFP_PRICE_RANGE;
    const index = findIndex(maxPriceForEachRange, (item) => item > priceValue);
    return index >= 0 ? convertedPriceRangeDFP[index] : [];
  }
  return [];
};

const convertLocationLevels = (location = {}) => {
  // @ts-ignore
  const { district = [{}], road = [{}], zone = [{}] } = location;
  return {
    location1: strengthenKebabCase(get(road, '[0].label')),
    location2: strengthenKebabCase(get(district, '[0].label')),
    location3: strengthenKebabCase(get(zone, '[0].label')),
  };
};

export const extractInt = (str: string) => {
  const numbers = str.match(/\d+/);
  return numbers ? parseInt(numbers[0]) : NaN;
};

const convertDFPBedroom = (roomTypes: RoomType[]) => {
  const roomTypesWithValue = map(
    filter(roomTypes, (item) => item.status === 'Y'),
    (item) => extractInt(item.name),
  );
  return isEmpty(filter(roomTypesWithValue, (n) => !isNaN(n)))
    ? {}
    : {
        minbed: `${min(roomTypesWithValue)}`,
        maxbed: `${max(roomTypesWithValue)}`,
      };
};

const convertDFPNearby = (transportations: Parent[]) => {
  return map(transportations, 'label');
};

export const buildIsTest = () => {
  const hostName = get(global, 'location.hostname');
  const isProdEnv =
    /^(www\.)?thinkofliving\.com$/.test(hostName) ||
    /^(www\.)?frontend-inactive.prod.thinkofliving\.com$/.test(hostName);

  return isProdEnv ? {} : { isTest: 'yes' };
};

export const buildTargetingArguments = (project = {}) => {
  const {
    transportations,
    location = {},
    priceTypes = {},
    roomTypes,
    id,
    developer,
  } = project as any;
  const { level = {} } = priceTypes;
  const { label = null } = level;

  let developerName = developer;
  if (isObject(developer)) {
    // @ts-ignore
    const { name = '' } = developer;
    developerName = name;
  }

  return omitBy(
    {
      ...convertLocationLevels(location),
      ...convertDFPBedroom(roomTypes),
      nearby: convertDFPNearby(transportations),
      price: convertDFPPrice(priceTypes),
      projectid: !isNil(id) ? id.toString() : null,
      developer: !isNil(developer) ? developerName : null,
      priceSegment: !isNil(level) ? label : null,
      ...buildIsTest(),
    },
    isEmpty,
  );
};

export const calculateAdsPosition = (
  adsPosition: number,
  adsIntervalOffset: number,
  totalLoadNumber: number,
  sect: string | string[] = DEFAULT_SECT,
  subSect = DEFAULT_SUBSECT,
  adsUnitNumber = MERC_UNIT_NUMBER,
  adsIntervalNumber = SHOW_MERC_INTERVAL_NUMBER,
) => {
  const ads = [];
  const headIntervalsNumber = adsIntervalNumber - adsIntervalOffset;
  const unitLength = adsIntervalNumber + 1;
  let newAdsIntervalOffset =
    adsIntervalNumber - (headIntervalsNumber - totalLoadNumber);
  let leftLoadNumber = totalLoadNumber - (headIntervalsNumber + 1);
  const getNumberIncrease = (
    number: number,
    max = Number.MAX_SAFE_INTEGER,
    min = 1,
  ) => {
    number++;
    return number > max ? min : number;
  };

  if (!(totalLoadNumber <= headIntervalsNumber)) {
    ads.push({
      index: headIntervalsNumber,
      position: [adsPosition],
      adUnit: AdUnits.AP_MrecInfiniteAd,
      sect: sect,
      subSect: subSect,
    });
    adsPosition = getNumberIncrease(adsPosition, adsUnitNumber);

    while (leftLoadNumber >= unitLength) {
      leftLoadNumber = leftLoadNumber - unitLength;
      ads.push({
        index: ads[ads.length - 1].index + unitLength,
        position: [adsPosition],
        adUnit: AdUnits.AP_MrecInfiniteAd,
        sect: sect,
        subSect: subSect,
      });
      adsPosition = getNumberIncrease(adsPosition, adsUnitNumber);
    }
    newAdsIntervalOffset = leftLoadNumber;
  }

  return {
    newAdsPosition: adsPosition,
    newAdsIntervalOffset,
    ads,
  };
};
/**
 * Create array containing ads along with posts
 * @param ads
 * @param posts
 * @param totalLoadNumber
 * @returns {[]}
 */
export const buildPostsList = (
  ads: { index: number }[],
  posts: unknown[],
  totalLoadNumber: number,
) => {
  let adsIndex = 0;
  let postIndex = 0;
  const result = [];

  for (let i = 0; i < totalLoadNumber; i++) {
    if (!isUndefined(ads[adsIndex]) && i === ads[adsIndex].index) {
      result.push(ads[adsIndex]);
      adsIndex++;
    } else {
      if (isUndefined(posts[postIndex])) break;
      result.push(posts[postIndex]);
      postIndex++;
    }
  }

  return result;
};

export function getAdSubsectByPropertyType(
  propertyType:
    | PropertyType.Condominium
    | PropertyType.House
    | PropertyType.Townhome
    | PropertyType.HolidayHomes
    | PropertyType.HomeOffice
    | PropertyType.MixedUse
    | PropertyType.Commercial,
) {
  return adSubsectByPropertyType.get(propertyType);
}

export function getAdSubsectByPostCategory(postCategory: PostSubcategory) {
  return adSubsectByPostCategory.get(postCategory);
}

export const getAdSectByPageType = (pageType: LandingPageTypes): string => {
  return adSectByPageType.get(pageType);
};

/**
 * Refresh the ads under the specified element
 * If element is not specified, will refresh all the ads.
 */
export const refreshAds = (element?: HTMLElement) => {
  if (element) {
    const adBoxNodes = element.querySelectorAll('.adBox');
    const slotIds = [...adBoxNodes].map((adElement) => adElement.id);
    logger.logInfo(
      'AdsHelper.refreshAds',
      `Refresh Ads with slots: ${JSON.stringify(slotIds)}`,
    );
    DFPManager.refresh(slotIds);
  } else {
    logger.logInfo('AdsHelper.refreshAds', 'Refresh all the ads in the page.');
    DFPManager.refresh();
  }
};

export const getAdPlatformType = (screenType: ScreenType) => {
  const PLATFORM_TYPE = {
    [SCREEN_TYPE.desktop]: 'web',
    [SCREEN_TYPE.smallDesktop]: 'web',
    [SCREEN_TYPE.tablet]: 'midi',
    [SCREEN_TYPE.mobile]: 'mobile',
  } as const;

  return PLATFORM_TYPE[screenType] ?? 'mobile';
};
